import React, {createContext, useCallback, useEffect, useMemo, useRef, useState} from 'react';
import PropTypes from 'prop-types';

import {useAccessToken, useCommerceApi} from '@salesforce/commerce-sdk-react';
import {useQuery} from '@tanstack/react-query';

import {
  resolveProductThumbnailUrl,
  resolveSwatchThumbnailUrl,
} from '@amblique/king-living/app/components/configuration-selector/utils';
import {
  getVariantSwatchImageUrl,
  mergePreselectedSelectionsFromProduct,
  sumSetCardSelectionPrices,
  sumSetCardSelectionListPrices,
} from '@amblique/king-living/app/utils/customize-selections';
import {chunk, GET_PRODUCTS_PAGE_SIZE, GET_PRODUCTS_PARAMS} from '@amblique/king-living/app/utils/api-utils';
import {setProductIncludesKingGuard} from '@amblique/king-living/app/utils/cart-utils';
import {useShopperInventoryIds} from '@amblique/king-living/app/hooks/use-shopper-inventory-ids';
import {normalizeCylindoFeatureToString} from '@amblique/king-living/app/utils/cylindo-params';
import {resolveCustomizeCartItemImageUrl} from '@amblique/king-living/app/utils/gallery-resource-utils';
import {getOptionsGroupingUIProcessed, getProductEffectivePrice, getProductListPrice, getProductPromotionalPrice} from '@amblique/king-living/app/utils/product-utils';
import {
  buildCustomizeByMasterSessionKey,
  clearCustomizeForProduct,
  persistCustomizeSelection,
  readCustomizeForProduct,
} from '@amblique/king-living/app/utils/session-utils';

/**
 * @typedef {Object} CoverSelection
 * @property {string} productId
 * @property {string|null} thumbnail
 * @property {Array} images
 * @property {number|null} price
 * @property {string|null} name
 * @property {Object} [variationValues]
 */

/**
 * @typedef {Object} OptionSelection
 * @property {string} productId
 * @property {string|null} thumbnail
 * @property {Array} images
 * @property {number|null} price
 * @property {string|null} name
 * @property {string|null} imageUrl
 */

/**
 * @typedef {Object} OptionalAddOns
 * @property {{ enabled: boolean }} kingGuard - KingGuard toggle state only (price/productId derived at runtime)
 * @property {Array<OptionSelection|null>} addons - Optional add-on product selections
 */

/**
 * @typedef {Object} SelectionsMap
 * @property {CoverSelection|null} [cover]
 * @property {Array<OptionSelection|null>} [containerId]  - option container slots (keyed by containerId)
 * @property {OptionalAddOns} [optionalAddOns]
 */

/**
 * @typedef {Object} CommittedSelection
 * @property {'set'|'customize'|null} mode
 * @property {string|null} productId - setId when mode='set', null when mode='customize'
 * @property {SelectionsMap} selections
 * @property {string|null} sourceSetId - original set before deviation
 * @property {boolean} deviatedFromSet
 */

/**
 * @typedef {Object} GalleryPreview
 * @property {Object|null} product - Product being previewed in the gallery
 * @property {Object} variationValues - Cover variation attributes for the preview
 */

/**
 * @typedef {Object} FlyoutDraftState
 * @property {boolean} isOpen
 * @property {string|null} editingProductId - drives flyout step structure
 * @property {SelectionsMap} draftSelections
 * @property {boolean} isDeviatingFromSet
 * @property {GalleryPreview|null} galleryPreview - Temporary gallery preview state (View Details / slot hover).
 *   Automatically cleared on cancelFlyout and commitFlyout.
 */

/**
 * @typedef {Object} SetCard
 * @property {string} productId
 * @property {string} name
 * @property {string|null} thumbnail
 * @property {number|null} price
 * @property {number|null} originalPrice
 * @property {string|null} badgeText
 * @property {string|null} currency
 */

/**
 * @typedef {Object.<string, Object>} SetProductsMap
 * Map of productId → full raw SCAPI product object for each linked set product.
 * Used by CustomizeStepsFlyout to resolve the correct product structure when
 * editing a set product (whose option containers may differ from masterA).
 */

const EMPTY_COMMITTED = {
  mode: null,
  productId: null,
  selections: {},
  sourceSetId: null,
  deviatedFromSet: false,
};

const EMPTY_FLYOUT_DRAFT = {
  isOpen: false,
  editingProductId: null,
  draftSelections: {},
  isDeviatingFromSet: false,
  hasUserInteracted: false,
  galleryPreview: null,
};

export const CustomizeJourneyContext = createContext(null);

/**
 * Clear KingGuard selection whenever selections change.
 * Forces customer to consciously opt-in to KingGuard after any configuration change.
 *
 * This prevents customers from accidentally purchasing KingGuard when:
 * - Switching between predefined sets
 * - Changing cover variants or option selections
 * - Editing existing configurations
 *
 * @param {SelectionsMap} selections - Current selections map
 * @returns {SelectionsMap} - Selections with KingGuard cleared
 */
function clearKingGuardSelection(selections) {
  if (!selections) return selections;

  const kingGuardEnabled = selections.optionalAddOns?.kingGuard?.enabled === true;

  // Always clear KingGuard to force customer reselection
  if (kingGuardEnabled) {
    return {
      ...selections,
      optionalAddOns: {
        ...selections.optionalAddOns,
        kingGuard: {enabled: false},
      },
    };
  }

  return selections;
}

function applyKingGuardClearPolicy(selections, linkedProduct) {
  if (!selections) return selections;
  if (setProductIncludesKingGuard(linkedProduct)) return selections;

  return clearKingGuardSelection(selections);
}

function carryAddOns(into, from) {
  const addons = from?.optionalAddOns?.addons;

  return addons?.some(Boolean) ?
    {...into, optionalAddOns: {...into.optionalAddOns, addons}} :
    into;
}

/**
 * Computes patch fields for a slot from a fetched product.
 * Returns a partial slot object, or null if nothing needs patching.
 *
 * Patches:
 * - imageUrl: same rule as buildOptionSlotSelectionFromProduct / picker
 *   (resolveProductThumbnailUrl — swatch → config image → imageGroups → product.image)
 * - price: effective price (if slot.price is null/0)
 * - cylindoFeature: c_cylindoFeature from product (if not yet set — undefined means unenriched,
 *   null means "product has no feature", both are valid after enrichment)
 *
 * @param {Object} slot
 * @param {Object} fetched - Full product from SCAPI
 * @returns {Object|null}
 */
function patchSlotData(slot, fetched) {
  const patch = {};
  const imgUrl = resolveProductThumbnailUrl(fetched);

  if (!slot.imageUrl && imgUrl) patch.imageUrl = imgUrl;

  const fetchedPrice = getProductEffectivePrice(fetched);

  if ((slot.price == null || slot.price === 0) && fetchedPrice != null) {
    patch.price = fetchedPrice;
  } else if (fetchedPrice != null && slot.price > 0 && fetchedPrice < slot.price) {
    if (slot.listPrice == null) {
      patch.listPrice = slot.price;
    }

    patch.price = fetchedPrice;
  }

  if (slot.listPrice == null) {
    const fetchedListPrice = getProductListPrice(fetched);

    if (fetchedListPrice != null) {
      patch.listPrice = fetchedListPrice;
    }
  }

  if (!('cylindoFeature' in slot)) {
    const rawFeature = fetched?.c_cylindoFeature ?? fetched?.cylindoFeature ?? null;

    patch.cylindoFeature = normalizeCylindoFeatureToString(rawFeature);
  }

  return Object.keys(patch).length > 0 ? patch : null;
}

function parseMasterAllVariantsData(masterProduct) {
  const raw = masterProduct?.c_allVariantsData;

  if (!raw) return null;

  try {
    const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;

    return Array.isArray(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

/**
 * Resolve cover from linked set `c_coverVariantId` (variation product id).
 *
 * @returns {Object|null}
 */
function resolveLinkedSetCoverContext(linkedProduct, masterProduct, allVariants) {
  const coverVariantProductId = linkedProduct?.c_coverVariantId;

  if (!coverVariantProductId) return null;

  const matchedVariant = masterProduct.variants?.find(
    (v) => String(v.productId) === String(coverVariantProductId)
  );
  const coverValue = matchedVariant?.variationValues?.cover ?? null;
  const coverAttr = masterProduct.variationAttributes?.find((a) => a.id === 'cover');
  const matchedAttrValue = coverValue ?
    coverAttr?.values?.find((v) => v.value === coverValue) :
    null;
  const avEntry = coverValue ?
    allVariants?.find((v) => v.variationValues?.cover === coverValue) ?? null :
    null;

  return {
    avEntry,
    coverValue,
    matchedAttrValue,
    matchedVariant,
    productIdFallback: coverVariantProductId,
  };
}

function resolveLinkedSetCoverVariantProductId(linkedProduct) {
  return linkedProduct?.c_coverVariantId ?? null;
}

/**
 * Build a SelectionsMap for a linked product (predefined set) by resolving its
 * c_coverVariantId and c_selectedOptions against the master product's variationAttributes,
 * variants, recommendations, and enriched optionsGroupingUI.
 *
 * @param {Object} linkedProduct - Linked product from SCAPI (the set)
 * @param {Object} masterProduct - Master product with variants and enriched OGU
 * @returns {SelectionsMap|null}
 */
export function buildDefaultSelectionsFromData(linkedProduct, masterProduct) {
  if (!linkedProduct || !masterProduct) return null;

  const selections = {};
  const allVariants = parseMasterAllVariantsData(masterProduct);
  const coverContext = resolveLinkedSetCoverContext(linkedProduct, masterProduct, allVariants);

  if (coverContext) {
    const {avEntry, coverValue, matchedAttrValue, matchedVariant, productIdFallback} = coverContext;

    selections.cover = {
      productId: matchedVariant?.productId || productIdFallback,
      name: matchedAttrValue?.name || null,
      variationValues: coverValue ? {cover: coverValue} : {},
      salePrice: avEntry?.salePrice ?? matchedVariant?.price ?? null,
      price: avEntry?.price ?? null,
      imageUrl:
        resolveSwatchThumbnailUrl(matchedVariant) ??
        (matchedVariant ? getVariantSwatchImageUrl(masterProduct, matchedVariant) : null),
    };
  }

  // --- Options: map c_selectedOptions → master enriched OGU ---
  const selectedOptions = linkedProduct.c_selectedOptions;

  if (!Array.isArray(selectedOptions) || selectedOptions.length === 0) {
    return Object.keys(selections).length > 0 ? selections : null;
  }

  // Build product-to-recommendation-type mapping AND product name lookup from the
  // master's enriched OGU (c_optionsGroupingUIProcessed). We use the enriched OGU
  // instead of product.recommendations because the PDP's useProduct hook does not
  // include expand=recommendations, so product.recommendations is typically empty.
  const masterOguContainers = getOptionsGroupingUIProcessed(masterProduct);
  const recTypeByProductId = {};
  const productNameMap = {};

  if (Array.isArray(masterOguContainers)) {
    masterOguContainers.forEach((container) => {
      (container.values || []).forEach((slot) => {
        const typeId = slot.recommendationTypeId;

        (slot.products || []).forEach((p) => {
          if (!p.productId) return;

          recTypeByProductId[p.productId] = {
            typeValue: typeId,
            typeName: slot.typeName || container.containerName || null,
            isHidden: slot.isVisible === false,
            price: p.price ?? null,
          };
          productNameMap[p.productId] = p.name || null;
        });
      });
    });
  }

  // Walk the master's enriched OGU — linked sets map selections via c_selectedOptions only.
  if (!Array.isArray(masterOguContainers)) {
    return Object.keys(selections).length > 0 ? selections : null;
  }

  masterOguContainers.forEach((container) => {
    const cid = container.containerId;

    if (!cid || cid === 'cover') return;

    const masterValues = container.values || [];
    const slots = new Array(Math.max(masterValues.length, 1)).fill(null);

    masterValues.forEach((slotDef, masterIdx) => {
      const slotTypeId = slotDef.recommendationTypeId;

      const matchedOptionId = selectedOptions.find((optId) => {
        const recInfo = recTypeByProductId[optId];

        return recInfo && recInfo.typeValue === slotTypeId;
      });

      if (!matchedOptionId) return;

      const recInfo = recTypeByProductId[matchedOptionId];
      const name = productNameMap[matchedOptionId] || recInfo.typeName || matchedOptionId;
      const slot = {productId: matchedOptionId, name, price: recInfo.price ?? 0, recommendationName: recInfo.typeName || null};

      if (recInfo.isHidden) {
        slot.isAutoSelected = true;
        slot.isHidden = true;
      }

      slots[masterIdx] = slot;
    });

    if (slots.some((s) => s != null)) {
      selections[cid] = slots;
    }
  });

  selections.optionalAddOns = {
    kingGuard: {enabled: setProductIncludesKingGuard(linkedProduct)},
    addons: [],
  };

  const result = Object.keys(selections).length > 0 ? selections : null;

  return result;
}

/**
 * Default + preselected selections for a predefined set (totals, thumbnail, selectSet).
 *
 * @param {Object} masterProduct
 * @param {Object} linkedProduct
 * @returns {SelectionsMap|null}
 */
export function getSetMergedSelections(masterProduct, linkedProduct) {
  if (!masterProduct || !linkedProduct) return null;

  const raw = buildDefaultSelectionsFromData(linkedProduct, masterProduct) || {};

  return mergePreselectedSelectionsFromProduct(masterProduct, raw);
}

function resolveSetCardThumbnailUrl(masterProduct, linkedProduct) {
  // Predefined-set swatch priority (configured on the linked set product):
  //   1. c_pimlyConfigurationImage[0]
  //   2. linked product SFCC imageGroups
  // (c_pimlyConfigurationImageSwatch is intentionally NOT used for predefined sets.)
  // resolveSwatchThumbnailUrl implements exactly this order.
  const linkedSwatchUrl = resolveSwatchThumbnailUrl(linkedProduct);

  if (linkedSwatchUrl) return linkedSwatchUrl;

  // Fallback only when the linked product carries no PIM/catalog image at all:
  // derive the configured hero image from the master + merged set selections.
  const merged = getSetMergedSelections(masterProduct, linkedProduct);

  if (!merged) return null;

  return resolveCustomizeCartItemImageUrl(masterProduct, merged, {requireCover: false});
}

/**
 * Normalise a raw product from getProducts into a SetCard.
 * defaultSelections are computed on-demand in editSet/selectSet to avoid
 * stale-closure issues where the queryFn captures an incomplete master product.
 *
 * @param {Object} product - Raw linked product from SCAPI
 * @returns {SetCard}
 */
/**
 * Validate that a linked product is properly configured to render as a "Choose Your Set" card.
 *
 * A linked (set) product is considered renderable only when `c_selectedOptions` is a
 * non-empty array of recommendation product IDs. Option step structure comes from the
 * master product's `c_optionsGroupingUIProcessed`.
 *
 * Linked products that fail validation are excluded from the set cards list to prevent
 * the "cover variant only" rendering bug — see docs/product/choose-your-sets-data-configuration.md
 *
 * @param {Object} linkedProduct - Raw linked product from SCAPI (cross_sell target)
 * @param {Object} masterProduct - PDP master with `c_optionsGroupingUIProcessed`
 * @returns {{isValid: boolean, reason: string|null}}
 */
function validateLinkedSetProduct(linkedProduct, masterProduct) {
  if (!linkedProduct) return {isValid: false, reason: 'missing-product'};

  const selectedOptions = linkedProduct.c_selectedOptions;

  if (!Array.isArray(selectedOptions) || selectedOptions.length === 0) {
    return {isValid: false, reason: 'missing-or-empty-c_selectedOptions'};
  }

  const masterOguContainers = getOptionsGroupingUIProcessed(masterProduct);

  if (!Array.isArray(masterOguContainers) || masterOguContainers.length === 0) {
    return {isValid: false, reason: 'missing-or-empty-master-ogu'};
  }

  return {isValid: true, reason: null};
}

function normaliseSetCard(linkedProduct) {
  let price = null;
  let originalPrice = null;
  const raw = linkedProduct?.c_priceProcessed;

  if (raw) {
    try {
      const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;

      price = parsed?.salePrice ?? null;
      originalPrice = parsed?.listPrice ?? null;
    } catch {
      // fall through
    }
  }
  if (price == null) {
    price = linkedProduct?.price ?? null;
  }

  return {
    productId: linkedProduct.id ?? linkedProduct.productId,
    name: linkedProduct.c_subRange ?? linkedProduct.name ?? '',
    thumbnail: null,
    price,
    originalPrice,
    badgeText: linkedProduct?.c_badgeText ?? linkedProduct?.badgeText ?? null,
    currency: linkedProduct.currency ?? null,
  };
}

/**
 * Determine if a given selections map deviates from a set's merged baseline.
 * The baseline (defaultSelections) must be the MERGED result of
 * buildDefaultSelectionsFromData + mergePreselectedSelectionsFromProduct
 * so that auto-selected hidden slots are included and won't create false positives.
 *
 * Deviation = any product ID in the draft differs from the baseline
 * (including going from a value to null or vice-versa), or cover variationValues differ.
 *
 * @param {SelectionsMap} draftSelections
 * @param {SelectionsMap|null} defaultSelections
 * @returns {boolean}
 */
function detectDeviation(draftSelections, defaultSelections) {
  if (!defaultSelections) return false;

  const draftVariantId = draftSelections.cover?.productId ?? null;
  const defaultVariantId = defaultSelections.cover?.productId ?? null;

  if (draftVariantId !== defaultVariantId) {
    return true;
  }

  const draftCoverValues = draftSelections.cover?.variationValues ?? {};
  const defaultCoverValues = defaultSelections.cover?.variationValues ?? {};
  const coverValueKeys = new Set([
    ...Object.keys(draftCoverValues),
    ...Object.keys(defaultCoverValues)
  ]);

  for (const key of coverValueKeys) {
    if (String(draftCoverValues[key] ?? '') !== String(defaultCoverValues[key] ?? '')) {
      return true;
    }
  }

  const allContainerIds = new Set([
    ...Object.keys(draftSelections).filter((k) => k !== 'cover'),
    ...Object.keys(defaultSelections).filter((k) => k !== 'cover')
  ]);

  for (const containerId of allContainerIds) {
    const draftSlots = draftSelections[containerId] || [];
    const defaultSlots = defaultSelections[containerId] || [];
    const maxLen = Math.max(draftSlots.length, defaultSlots.length);

    for (let i = 0; i < maxLen; i++) {
      const draftProductId = draftSlots[i]?.productId ?? null;
      const defaultProductId = defaultSlots[i]?.productId ?? null;

      if (draftProductId !== defaultProductId) {
        return true;
      }
    }
  }

  return false;
}

/**
 * Provider that manages the full customize journey state:
 * - committedSelection: persists through flyout cancel
 * - flyoutDraftState: temporary editing state, discarded on cancel
 * - setCards: linked products for "Choose your set"
 *
 * Panel header info (step title, back navigation) is intentionally NOT stored here —
 * it is pure UI state managed locally by each consumer (ConfigurationSelector, CustomizeStepsInline).
 *
 * @param {Object} props
 * @param {Object} props.product - Master product (with productLinks from expand=links)
 * @param {React.ReactNode} props.children
 */
export function CustomizeJourneyProvider({product, siteId, children}) {
  const api = useCommerceApi();
  const {getTokenWhenReady} = useAccessToken();
  const shopperInventoryIds = useShopperInventoryIds();

  const productId = product?.id ?? product?.productId ?? null;
  const sessionKey = buildCustomizeByMasterSessionKey(siteId);

  const [committedSelection, setCommittedSelection] = useState(() => {
    if (typeof window === 'undefined' || !productId) return EMPTY_COMMITTED;

    return readCustomizeForProduct(sessionKey, productId) ?? EMPTY_COMMITTED;
  });
  const [flyoutDraftState, setFlyoutDraftState] = useState(EMPTY_FLYOUT_DRAFT);

  /**
   * Live inline PDP customize draft (CustomizeStepsInline). Null when that shell is not mounted.
   * Used so consumers like AddToCartStrip can show the same selection summary as the form before Done.
   */
  const [inlineSelectionsDraft, setInlineSelectionsDraft] = useState(null);

  // Double-submit protection flag
  const isCommittingRef = useRef(false);

  // Extract cross-sell linked product IDs from SCAPI's native productLinks
  // (returned when the PDP fetches with expand=links). Only cross_sell links
  // are used — they represent predefined "Choose Your Set" bundles.
  const linkedProductIds = useMemo(() => {
    const links = product?.productLinks;

    if (!Array.isArray(links) || links.length === 0) return [];

    // SCAPI returns cross-sell productLinks in the reverse of the Business
    // Manager "Links" grid order, so reverse them to match the BM-configured
    // order shown to merchants.
    return links
      .filter((link) => link.type === 'cross_sell')
      .map((link) => link.targetProductId)
      .filter(Boolean)
      .reverse();
  }, [product]);
  const linkedIdsKey = linkedProductIds.join(',');

  const {
    data: linkedProductsData,
    fetchStatus: linkedProductsFetchStatus,
  } = useQuery({
    queryKey: ['king-living/linkedProducts', productId, linkedIdsKey, shopperInventoryIds ?? ''],
    queryFn: async () => {
      if (linkedProductIds.length === 0) return {cards: [], productsMap: {}};
      const token = await getTokenWhenReady();
      const chunks = chunk(linkedProductIds, GET_PRODUCTS_PAGE_SIZE);
      const results = await Promise.all(
        chunks.map((ids) => api.shopperProducts
          .getProducts({
            parameters: {
              ids: ids.join(','),
              ...GET_PRODUCTS_PARAMS,
              ...(shopperInventoryIds ? {inventoryIds: shopperInventoryIds} : {}),
            },
            headers: {Authorization: `Bearer ${token}`},
          })
          .then((res) => res?.data ?? []))
      );
      const rawProducts = results.flat();
      const productsMap = {};

      rawProducts.forEach((p) => {
        const {isValid, reason} = validateLinkedSetProduct(p, product);

        if (!isValid) {
          if (typeof console !== 'undefined') {
            console.warn(
              '[ChooseYourSets] Skipping linked product — invalid configuration.',
              {
                productId: p?.id ?? p?.productId,
                masterProductId: product?.id ?? product?.productId,
                reason,
              }
            );
          }

          return;
        }

        const id = p.id ?? p.productId;

        if (id) {
          productsMap[id] = p;
        }
      });

      // Preserve the BM-configured cross-sell order. SCAPI's getProducts does
      // not guarantee the response order matches the requested ids, so build
      // the cards by iterating linkedProductIds (which follows the productLinks
      // order defined in Business Manager) and skipping any that were invalid.
      const orderedValidProducts = linkedProductIds
        .map((id) => productsMap[id])
        .filter(Boolean);

      return {
        cards: orderedValidProducts.map((p) => normaliseSetCard(p)),
        productsMap,
      };
    },
    enabled: linkedProductIds.length > 0,
    staleTime: 5 * 60 * 1000,
  });

  // React Query v4 reports isLoading=true when enabled=false and there's no cached data.
  // Derive loading state from fetchStatus to avoid showing skeletons when there are no linked products.
  const isLoadingSetCards = linkedProductsFetchStatus === 'fetching';
  const setCards = linkedProductsData?.cards ?? [];
  const setProductsMap = linkedProductsData?.productsMap ?? {};

  // ---------------------------------------------------------------------------
  // Eager option-product price fetch for set card totals.
  // OGU product entries (c_optionsGroupingUIProcessed) may have null prices.
  // Fetching the actual SCAPI products resolves real prices at load time,
  // mirroring what fetchAndPatchImages does for committed/draft selections.
  // ---------------------------------------------------------------------------
  const setOptionProductIds = useMemo(() => {
    const ids = new Set();

    Object.values(setProductsMap).forEach((lp) => {
      const opts = lp?.c_selectedOptions;

      if (Array.isArray(opts)) {
        opts.forEach((id) => {
          if (id) ids.add(id);
        });
      }

      const coverVariantProductId = resolveLinkedSetCoverVariantProductId(lp);

      if (coverVariantProductId) ids.add(coverVariantProductId);
    });

    return Array.from(ids);
  }, [setProductsMap, product]);

  const setOptionIdsKey = setOptionProductIds.join(',');

  const {data: setOptionPriceMaps, fetchStatus: setOptionPricesFetchStatus} = useQuery({
    queryKey: ['king-living/setOptionPrices', productId, setOptionIdsKey, shopperInventoryIds ?? ''],
    queryFn: async () => {
      const token = await getTokenWhenReady();
      const batches = chunk(setOptionProductIds, GET_PRODUCTS_PAGE_SIZE);
      const results = await Promise.all(
        batches.map((ids) => api.shopperProducts
          .getProducts({
            parameters: {
              ids: ids.join(','),
              ...GET_PRODUCTS_PARAMS,
              ...(shopperInventoryIds ? {inventoryIds: shopperInventoryIds} : {}),
            },
            headers: {Authorization: `Bearer ${token}`},
          })
          .then((res) => res?.data ?? []))
      );

      const priceMap = {};
      const listPriceMap = {};
      const imageMap = {};
      const cylindoMap = {};

      results.flat().forEach((p) => {
        const id = p.id ?? p.productId;

        if (id) {
          priceMap[id] = getProductEffectivePrice(p) ?? null;
          const lp = getProductListPrice(p);

          if (lp != null) listPriceMap[id] = lp;

          // Options use the same pimlyConfigurationImage-first swatch rule as the predefined
          // set + cover: c_pimlyConfigurationImage[0] → imageGroups (NOT the swatch attribute).
          const imgUrl = resolveSwatchThumbnailUrl(p);

          if (imgUrl) imageMap[id] = imgUrl;

          const rawFeature = p.c_cylindoFeature ?? p.cylindoFeature ?? null;

          cylindoMap[id] = normalizeCylindoFeatureToString(rawFeature);
        }
      });

      return {priceMap, listPriceMap, imageMap, cylindoMap};
    },
    enabled: setOptionProductIds.length > 0,
    staleTime: 5 * 60 * 1000,
  });

  const isFetchingSetOptionPrices = setOptionPricesFetchStatus === 'fetching';
  const setOptionPriceMap = setOptionPriceMaps?.priceMap;
  const setOptionListPriceMap = setOptionPriceMaps?.listPriceMap;
  const setOptionImageMap = setOptionPriceMaps?.imageMap ?? {};
  const setOptionCylindoMap = setOptionPriceMaps?.cylindoMap ?? {};

  // Cache cover enrichment by productId — skips re-fetch on subsequent set selections.
  const coverEnrichmentRef = useRef({});

  // Per-card totals: same source as `selectSet` (buildDefaultSelectionsFromData + merge) so the
  // collapsed card matches the expanded/selected total. SCAPI option prices patch missing OGU prices.
  const setCardTotals = useMemo(() => {
    const totals = {};

    for (const card of setCards) {
      const isCommitted =
        committedSelection.mode === 'set' &&
        committedSelection.productId === card.productId;

      if (isCommitted) {
        const total = sumSetCardSelectionPrices(committedSelection.selections, setOptionPriceMap);

        if (total > 0) totals[card.productId] = total;

        continue;
      }

      const linkedProduct = setProductsMap[card.productId];

      if (!linkedProduct || !product) continue;

      const merged = getSetMergedSelections(product, linkedProduct);

      if (!merged) continue;

      const total = sumSetCardSelectionPrices(merged, setOptionPriceMap);

      if (total > 0) totals[card.productId] = total;
    }

    return totals;
  }, [committedSelection, product, setCards, setProductsMap, setOptionPriceMap]);

  // Per-card list totals (RRP) for strikethrough. Returns 0 per card when no discount exists.
  const setCardListTotals = useMemo(() => {
    const totals = {};

    for (const card of setCards) {
      const isCommitted =
        committedSelection.mode === 'set' &&
        committedSelection.productId === card.productId;

      if (isCommitted) {
        const listTotal = sumSetCardSelectionListPrices(committedSelection.selections, setOptionListPriceMap);

        if (listTotal > 0) totals[card.productId] = listTotal;
        continue;
      }

      const linkedProduct = setProductsMap[card.productId];

      if (!linkedProduct || !product) continue;

      const merged = getSetMergedSelections(product, linkedProduct);

      if (!merged) continue;

      const listTotal = sumSetCardSelectionListPrices(merged, setOptionListPriceMap);

      if (listTotal > 0) totals[card.productId] = listTotal;
    }

    return totals;
  }, [committedSelection, product, setCards, setProductsMap, setOptionListPriceMap]);

  const enrichedSetCards = useMemo(() => {
    if (setCards.length === 0 || !product) return setCards;

    return setCards.map((card) => {
      const linkedProduct = setProductsMap[card.productId];

      if (!linkedProduct) return card;

      const url = resolveSetCardThumbnailUrl(product, linkedProduct);

      return url ? {...card, thumbnail: url} : card;
    });
  }, [setCards, setProductsMap, product]);

  /**
   * Select a pre-configured set. Updates committedSelection to mode='set'.
   * Loads the set's defaultSelections from the matching SetCard.
   *
   * @param {string} setId - Product ID of the set to select
   */
  const selectSet = useCallback(
    (setId) => {
      const linkedProduct = setProductsMap[setId];
      const merged = getSetMergedSelections(product, linkedProduct) ?? {};

      const preEnrichedSelections = {};

      Object.entries(merged).forEach(([slotKey, slotOrSlots]) => {
        if (slotKey === 'cover' || !Array.isArray(slotOrSlots)) {
          preEnrichedSelections[slotKey] = slotOrSlots;

          return;
        }

        const imgMap = setOptionImageMap;
        const cylMap = setOptionCylindoMap;
        const priceMap = setOptionPriceMap ?? null;
        const listPriceMap = setOptionListPriceMap ?? null;

        preEnrichedSelections[slotKey] = slotOrSlots.map((slot) => {
          if (!slot?.productId) return slot;

          const pid = slot.productId;
          const patch = {};

          if (!slot.imageUrl && imgMap?.[pid]) patch.imageUrl = imgMap[pid];
          if (!('cylindoFeature' in slot) && pid in cylMap) patch.cylindoFeature = cylMap[pid];

          const priceFromMap = priceMap?.[pid];

          if ((slot.price == null || slot.price === 0) && priceFromMap != null) {
            patch.price = priceFromMap;
          }

          const listPriceFromMap = listPriceMap?.[pid];

          if (slot.listPrice == null && listPriceFromMap != null) patch.listPrice = listPriceFromMap;

          return Object.keys(patch).length ? {...slot, ...patch} : slot;
        });
      });

      // Restore cached cover enrichment so fetchAndPatchImages skips the cover fetch.
      const coverId = preEnrichedSelections.cover?.productId;

      if (coverId && coverEnrichmentRef.current[coverId]) {
        preEnrichedSelections.cover = {
          ...preEnrichedSelections.cover,
          ...coverEnrichmentRef.current[coverId],
        };
      }

      const selectionsWithClearedKingGuard = applyKingGuardClearPolicy(preEnrichedSelections, linkedProduct);

      // Clear customize session when selecting a predefined set
      clearCustomizeForProduct(sessionKey, productId);

      setCommittedSelection((prev) => ({
        mode: 'set',
        productId: setId,
        selections: carryAddOns(selectionsWithClearedKingGuard, prev.selections),
        sourceSetId: setId,
        deviatedFromSet: false,
      }));
    },
    [setProductsMap, product, productId, sessionKey, setOptionImageMap, setOptionCylindoMap, setOptionPriceMap, setOptionListPriceMap]
  );

  /**
   * Select a set AND open the flyout for editing in one atomic operation.
   *
   * Computes defaultSelections on-demand from the linked product (setProductsMap)
   * and the CURRENT master product — avoids stale-closure issues where the
   * queryFn captured an incomplete master product at query time.
   *
   * Uses the master product for mergePreselectedSelectionsFromProduct because the
   * master has the enriched OGU with slot isVisible / isRequired flags needed
   * for proper hidden-slot auto-selection.
   *
   * @param {string} setId - Product ID of the set to edit
   */
  const editSet = useCallback(
    (setId) => {
      const linkedProduct = setProductsMap[setId];
      const sameSetAlreadyCommitted =
        committedSelection.mode === 'set' &&
        committedSelection.productId === setId;

      const merged = sameSetAlreadyCommitted ?
        committedSelection.selections :
        getSetMergedSelections(product, linkedProduct) ?? {};

      const selectionsWithClearedKingGuard = applyKingGuardClearPolicy(merged, linkedProduct);
      const nextSelections = carryAddOns(selectionsWithClearedKingGuard, committedSelection.selections);

      // Clear customize session when editing a predefined set
      clearCustomizeForProduct(sessionKey, productId);

      setCommittedSelection({
        mode: 'set',
        productId: setId,
        selections: nextSelections,
        sourceSetId: setId,
        deviatedFromSet: sameSetAlreadyCommitted ?
          committedSelection.deviatedFromSet :
          false,
      });

      const draftSelections = sameSetAlreadyCommitted ?
        applyKingGuardClearPolicy(structuredClone(committedSelection.selections), linkedProduct) :
        nextSelections;

      setFlyoutDraftState({
        isOpen: true,
        editingProductId: setId,
        draftSelections,
        isDeviatingFromSet: false,
        hasUserInteracted: false,
        galleryPreview: null,
      });
    },
    [
      committedSelection.deviatedFromSet,
      committedSelection.mode,
      committedSelection.productId,
      committedSelection.selections,
      product,
      productId,
      setProductsMap,
      sessionKey
    ]
  );

  /**
   * Switch the flyout from a pre-defined set back to free "Customise your own" mode.
   * Resets committedSelection to 'customize' and re-seeds the flyout draft with
   * fresh preselected options from the master product.
   * Only meaningful when the flyout is already open.
   */
  const switchToCustomizeInFlyout = useCallback(() => {
    setCommittedSelection({
      mode: 'customize',
      productId, // Master/primary product ID
      selections: carryAddOns({}, committedSelection.selections),
      sourceSetId: null,
      deviatedFromSet: false,
    });

    const merged = mergePreselectedSelectionsFromProduct(
      product,
      carryAddOns({}, committedSelection.selections)
    );

    setFlyoutDraftState((prev) => ({
      ...prev,
      editingProductId: productId,
      draftSelections: merged,
      isDeviatingFromSet: false,
      hasUserInteracted: false,
      galleryPreview: null,
    }));
  }, [committedSelection.selections, product, productId]);

  /**
   * Open the customize flyout for editing.
   *
   * When transitioning from a pre-defined set to "Customize Your Own" (the
   * editingProductId is the master, not a known set product), start with blank
   * selections so previous set choices don't carry over.
   * Otherwise seeds from committedSelection.selections.
   *
   * @param {string} editingProductId - Product ID whose step structure drives the flyout
   */
  const openFlyout = useCallback(
    (editingProductId) => {
      const isOpeningForSetProduct = setProductsMap[editingProductId] != null;
      const wasInSetMode = committedSelection.mode === 'set';
      const isSwitchingToCustomize = !isOpeningForSetProduct && wasInSetMode;

      const baseSelections = isSwitchingToCustomize ?
        carryAddOns({}, committedSelection.selections) :
        committedSelection.selections;

      // When switching from a predefined set to customize, immediately clear the
      // committed set selection. This ensures the set is gone even if the user
      // cancels the flyout without clicking Done.
      if (isSwitchingToCustomize) {
        setCommittedSelection({
          mode: 'customize',
          productId,
          selections: baseSelections,
          sourceSetId: null,
          deviatedFromSet: false,
        });
      }

      const editingProduct = setProductsMap[editingProductId] || product;
      const merged = mergePreselectedSelectionsFromProduct(editingProduct, baseSelections);

      setFlyoutDraftState({
        isOpen: true,
        editingProductId,
        draftSelections: merged,
        isDeviatingFromSet: false,
        hasUserInteracted: false,
        galleryPreview: null,
      });
    },
    [committedSelection.mode, committedSelection.selections, product, productId, setProductsMap]
  );

  /**
   * Check if mode change requires user confirmation.
   * Returns true when switching between modes/sets with active selections.
   *
   * Checks BOTH:
   * 1. Committed selections (after Done)
   * 2. Draft selections (user actively editing in flyout, not just preselected defaults)
   *
   * @param {string} targetMode - 'set' | 'customize'
   * @param {string|null} targetProductId - Set ID or master ID
   * @returns {boolean} - true if should show confirmation modal
   */
  const shouldConfirmModeChange = useCallback(
    (targetMode, targetProductId) => {
      // No-op: switching between predefined sets never requires confirmation (FSD PDP-07).
      if (targetMode === 'set' && committedSelection.mode === 'set') {
        return false;
      }

      // No-op: already on the same set — no change.
      if (
        targetMode === 'set' &&
        String(committedSelection.productId) === String(targetProductId)
      ) {
        return false;
      }

      // No-op: already in customize mode — no change.
      if (targetMode === 'customize' && committedSelection.mode === 'customize') {
        return false;
      }

      // Committed to customize mode → switching to a predefined set always warns.
      // Even a clean customize (no selections) is an explicit mode commitment.
      if (committedSelection.mode === 'customize' && targetMode === 'set') {
        return true;
      }

      // For all other transitions, warn only when there are active selections to lose.
      const hasCommittedSelections =
        committedSelection.mode != null &&
        Object.keys(committedSelection.selections || {}).length > 0;

      const hasDraftSelections =
        flyoutDraftState.isOpen &&
        flyoutDraftState.hasUserInteracted;

      return hasCommittedSelections || hasDraftSelections;
    },
    [committedSelection, flyoutDraftState]
  );

  /**
   * Attempt to select a set with confirmation check.
   * If confirmation needed, calls onNeedsConfirm callback instead of selecting.
   *
   * @param {string} setId - Product ID of the set to select
   * @param {Function} [onNeedsConfirm] - Callback when confirmation is needed
   * @returns {{needsConfirm: boolean}} - Whether confirmation is needed
   */
  const selectSetSafe = useCallback(
    (setId, onNeedsConfirm) => {
      if (shouldConfirmModeChange('set', setId)) {
        onNeedsConfirm?.();

        return {needsConfirm: true};
      }

      selectSet(setId);

      return {needsConfirm: false};
    },
    [shouldConfirmModeChange, selectSet]
  );

  /**
   * Attempt to open customize flyout with confirmation check.
   * If confirmation needed, calls onNeedsConfirm callback instead of opening.
   *
   * @param {string} productId - Product ID to edit (usually master product)
   * @param {Function} [onNeedsConfirm] - Callback when confirmation is needed
   * @returns {{needsConfirm: boolean}} - Whether confirmation is needed
   */
  const openFlyoutSafe = useCallback(
    (productId, onNeedsConfirm) => {
      if (shouldConfirmModeChange('customize', productId)) {
        onNeedsConfirm?.();

        return {needsConfirm: true};
      }

      openFlyout(productId);

      return {needsConfirm: false};
    },
    [shouldConfirmModeChange, openFlyout]
  );

  /**
   * Attempt to edit a set with confirmation check.
   * If confirmation needed, calls onNeedsConfirm callback instead of editing.
   *
   * @param {string} setId - Product ID of the set to edit
   * @param {Function} [onNeedsConfirm] - Callback when confirmation is needed
   * @returns {{needsConfirm: boolean}} - Whether confirmation is needed
   */
  const editSetSafe = useCallback(
    (setId, onNeedsConfirm) => {
      if (shouldConfirmModeChange('set', setId)) {
        onNeedsConfirm?.();

        return {needsConfirm: true};
      }

      editSet(setId);

      return {needsConfirm: false};
    },
    [shouldConfirmModeChange, editSet]
  );

  /**
   * Update a single step's selection in the flyout draft.
   * Automatically detects deviation from the current set's defaults.
   *
   * @param {string} stepId - 'cover' or containerId
   * @param {*} value - New selection value for the step
   * @param {boolean} [silent=false] - When true, does not mark hasUserInteracted (used for
   *   system-driven auto-selections such as hidden slot auto-fill).
   */
  const updateDraftSelection = useCallback(
    (stepId, value, silent = false) => {
      setFlyoutDraftState((prev) => {
        const newDraftSelections = {...prev.draftSelections, [stepId]: value};

        // Detect deviation from the committed set's defaults.
        // Silent calls (system-driven, e.g. hidden-slot auto-fill) must NOT
        // update isDeviatingFromSet — they are not user choices and should
        // never mark the selection as having deviated from the preset.
        let isDeviatingFromSet = prev.isDeviatingFromSet;

        if (!silent && committedSelection.mode === 'set') {
          isDeviatingFromSet = detectDeviation(newDraftSelections, committedSelection.selections);
        }

        return {
          ...prev,
          draftSelections: newDraftSelections,
          isDeviatingFromSet,
          hasUserInteracted: silent ? prev.hasUserInteracted : true,
        };
      });
    },
    [committedSelection.mode, committedSelection.selections]
  );

  /**
   * Mark that the user has actively interacted with the flyout (e.g. entered a sub-panel).
   * This is separate from updateDraftSelection which handles actual selections.
   * Used to show the Next button after the user opens a slot picker, even before confirming.
   */
  const markFlyoutUserInteracted = useCallback(() => {
    setFlyoutDraftState((prev) => {
      if (prev.hasUserInteracted) return prev;

      return {...prev, hasUserInteracted: true};
    });
  }, []);

  /**
   * Commit flyout changes to committedSelection and close the flyout.
   *
   * The resulting mode is determined by what the flyout was editing:
   * - If editingProductId is a known set (in setProductsMap) AND no deviation → 'set'
   * - Otherwise → 'customize'
   *
   * This correctly handles the case where the user was in 'set' mode but
   * opened the flyout from the "Customize your own" trigger: editingProductId
   * would be the master product (not in setProductsMap), so mode becomes 'customize'.
   */
  const commitFlyout = useCallback(() => {
    // Double-submit protection
    if (isCommittingRef.current) return;

    isCommittingRef.current = true;

    try {
      setFlyoutDraftState((prev) => {
        const {draftSelections, isDeviatingFromSet, editingProductId} = prev;

        setCommittedSelection((current) => {
          const isEditingKnownSet =
            editingProductId != null && setProductsMap[editingProductId] != null;
          const shouldBeSetMode = isEditingKnownSet && !isDeviatingFromSet;
          const linkedProduct = shouldBeSetMode && editingProductId != null ?
            setProductsMap[editingProductId] :
            null;
          const selectionsWithClearedKingGuard = applyKingGuardClearPolicy(
            draftSelections,
            linkedProduct
          );

          const newSelection = {
            mode: shouldBeSetMode ? 'set' : 'customize',
            productId: shouldBeSetMode ? editingProductId : productId,
            selections: selectionsWithClearedKingGuard,
            sourceSetId: shouldBeSetMode ?
              editingProductId :
              (current.mode === 'set' ? current.productId : current.sourceSetId),
            deviatedFromSet: !shouldBeSetMode && current.mode === 'set',
          };

          // Persist to session ONLY when user clicks "Done"
          persistCustomizeSelection(sessionKey, productId, newSelection);

          return newSelection;
        });

        return EMPTY_FLYOUT_DRAFT;
      });
    } finally {
      // Reset flag after a short delay to allow state updates to complete
      setTimeout(() => {
        isCommittingRef.current = false;
      }, 100);
    }
  }, [setProductsMap, sessionKey, productId]);

  /**
   * Cancel flyout editing — discard draft, restore isOpen=false.
   * committedSelection is NOT changed.
   */
  const cancelFlyout = useCallback(() => {
    setFlyoutDraftState(EMPTY_FLYOUT_DRAFT);
  }, []);

  /**
   * Commit inline selections directly to committedSelection.
   * Used by the inline PDP view and optional add-ons (including KingGuard toggle).
   * Does not touch flyoutDraftState.
   *
   * NOTE: Does NOT clear KingGuard here because this function is also used by
   * the KingGuard toggle itself. Clearing would break the toggle functionality.
   *
   * @param {SelectionsMap} selections - The inline draft selections to commit
   */
  const commitInlineSelections = useCallback((selections) => {
    // Double-submit protection
    if (isCommittingRef.current) return;

    isCommittingRef.current = true;

    try {
      setCommittedSelection((prev) => {
        const newSelection = {
          ...prev,
          mode: prev.mode,
          // Preserve the set product ID when in 'set' mode — selectSet/editSet
          // set productId to the linked product's ID, not the master's. This keeps the
          // selection state stable when the user later adds optional add-ons.
          // Only update to the master productId in 'customize' mode.
          productId: prev.mode === 'set' ? prev.productId : productId,
          selections,
        };

        // Persist to session ONLY when user clicks "Done"
        persistCustomizeSelection(sessionKey, productId, newSelection);

        return newSelection;
      });
    } finally {
      // Reset flag after a short delay to allow state updates to complete
      setTimeout(() => {
        isCommittingRef.current = false;
      }, 100);
    }
  }, [sessionKey, productId]);

  /**
   * Clear all selections and reset to initial state.
   * Also clears session storage to prevent stale data from being restored.
   */
  const resetJourney = useCallback(() => {
    if (productId) clearCustomizeForProduct(sessionKey, productId);

    setCommittedSelection(EMPTY_COMMITTED);
    setFlyoutDraftState(EMPTY_FLYOUT_DRAFT);
    setInlineSelectionsDraft(null);
  }, [sessionKey, productId]);

  /**
   * Set a temporary gallery preview while the flyout is open.
   * Used by "View Details" clicks and slot/cover hover previews.
   * Automatically cleared when the flyout is cancelled or committed.
   *
   * @param {GalleryPreview|null} preview - Preview state, or null to clear
   */
  const updateGalleryPreview = useCallback((preview) => {
    setFlyoutDraftState((prev) => ({
      ...prev,
      galleryPreview: preview ?
        {product: preview.product ?? null, variationValues: preview.variationValues ?? {}} :
        null,
    }));
  }, []);

  // ---------------------------------------------------------------------------
  // Async image enrichment for pre-selected option slots.
  // Shared fetch logic for both flyout draft and committed selections.
  // ---------------------------------------------------------------------------
  const fetchAndPatchImages = useCallback(
    async (selections, patchStateFn) => {
      const idsToFetch = [];

      // Cover variant: fetch to enrich listPrice and promo pricing; skip if already cached.
      const cover = selections.cover;

      if (cover?.productId && !coverEnrichmentRef.current[cover.productId]) {
        idsToFetch.push(cover.productId);
      }

      Object.entries(selections).forEach(([stepId, stepValue]) => {
        if (stepId === 'cover' || !Array.isArray(stepValue)) return;

        stepValue.forEach((slot) => {
          if (!slot?.productId) return;

          const needsImage = !slot.imageUrl;
          const needsPrice = slot.price == null || slot.price === 0;
          // cylindoFeature undefined = slot built by buildDefaultSelectionsFromData (predefined set),
          // not yet enriched — must fetch so V2 layering gets the correct Cylindo feature.
          const needsCylindoFeature = !('cylindoFeature' in slot);

          if (needsImage || needsPrice || needsCylindoFeature) {
            idsToFetch.push(slot.productId);
          }
        });
      });

      if (idsToFetch.length === 0) return;

      try {
        const token = await getTokenWhenReady();
        const batches = chunk(idsToFetch, GET_PRODUCTS_PAGE_SIZE);
        const results = await Promise.all(
          batches.map((ids) => api.shopperProducts
            .getProducts({
              parameters: {
                ids: ids.join(','),
                ...GET_PRODUCTS_PARAMS,
                ...(shopperInventoryIds ? {inventoryIds: shopperInventoryIds} : {}),
              },
              headers: {Authorization: `Bearer ${token}`},
            })
            .then((res) => res?.data ?? []))
        );

        const fetchedMap = {};

        results.flat().forEach((p) => {
          const id = p.id ?? p.productId;

          if (id) fetchedMap[id] = p;
        });

        patchStateFn(fetchedMap);
      } catch {
        // Image enrichment is cosmetic; failures are non-critical
      }
    },
    [api, getTokenWhenReady, shopperInventoryIds]
  );

  // Flyout draft enrichment: triggered when editing a new set in the flyout.
  // fetchAndPatchImagesRef keeps a stable ref so the effect only re-runs when
  // isOpen/editingProductId change — not on every gallery preview update.
  const draftSelectionsRef = useRef(flyoutDraftState.draftSelections);

  draftSelectionsRef.current = flyoutDraftState.draftSelections;

  const fetchAndPatchImagesRef = useRef(fetchAndPatchImages);

  fetchAndPatchImagesRef.current = fetchAndPatchImages;

  /**
   * When draft selections gain new option rows (e.g. name-synced sibling slots) without
   * imageUrl, we must re-run fetch. Depends on draft content, not only flyout open/id —
   * gallery preview does not mutate draftSelections, so this stays stable during picker hover.
   */
  const flyoutDraftImageFetchKey = useMemo(() => {
    if (!flyoutDraftState.isOpen || !flyoutDraftState.editingProductId) {
      return '';
    }

    const selections = flyoutDraftState.draftSelections;

    if (!selections || typeof selections !== 'object') {
      return '';
    }

    const parts = [];

    // Collect slots that need enrichment (missing imageUrl, price, or cylindoFeature)
    Object.entries(selections).forEach(([stepId, stepValue]) => {
      if (stepId === 'cover' || !Array.isArray(stepValue)) return;

      stepValue.forEach((slot, idx) => {
        if (!slot?.productId) return;

        const needsEnrichment =
          !slot.imageUrl || slot.price == null || slot.price === 0 || !('cylindoFeature' in slot);

        if (needsEnrichment) {
          parts.push(`${stepId}:${idx}:${slot.productId}`);
        }
      });
    });

    return parts.sort().join('|');
  }, [flyoutDraftState.isOpen, flyoutDraftState.editingProductId, flyoutDraftState.draftSelections]);

  useEffect(() => {
    if (!flyoutDraftState.isOpen || !flyoutDraftState.editingProductId) return;

    const selections = draftSelectionsRef.current;

    if (!selections) return;

    fetchAndPatchImagesRef.current(selections, (fetchedMap) => {
      setFlyoutDraftState((prev) => {
        const updated = {...prev.draftSelections};
        let changed = false;

        // Enrich cover: list price from c_priceProcessed, sale price from promotions
        if (updated.cover?.productId) {
          const fetchedCover = fetchedMap[updated.cover.productId];

          if (fetchedCover) {
            const coverPatch = {};

            if (updated.cover.price == null) {
              const coverListPrice = getProductListPrice(fetchedCover);

              if (coverListPrice != null) coverPatch.price = coverListPrice;
            }

            const coverPromoPrice = getProductPromotionalPrice(fetchedCover);
            const currentSale = updated.cover.salePrice ?? updated.cover.price;

            if (coverPromoPrice != null && (currentSale == null || coverPromoPrice < currentSale)) {
              coverPatch.salePrice = coverPromoPrice;
            }

            // Override the cover swatch with the variant's c_pimlyConfigurationImage[0] now that the
            // full cover product is available (same pimlyConfigurationImage-first rule as set/options).
            const coverImg = resolveSwatchThumbnailUrl(fetchedCover);

            if (coverImg && coverImg !== updated.cover.imageUrl) coverPatch.imageUrl = coverImg;

            if (Object.keys(coverPatch).length > 0) {
              updated.cover = {...updated.cover, ...coverPatch};
              changed = true;
            }
          }
        }

        Object.entries(updated).forEach(([stepId, slotArr]) => {
          if (stepId === 'cover' || !Array.isArray(slotArr)) return;

          updated[stepId] = slotArr.map((slot) => {
            if (!slot?.productId) return slot;

            const fetched = fetchedMap[slot.productId];

            if (!fetched) return slot;

            const patch = patchSlotData(slot, fetched);

            if (!patch) return slot;

            changed = true;

            return {...slot, ...patch};
          });
        });

        if (!changed) return prev;

        return {...prev, draftSelections: updated};
      });
    });
  }, [flyoutDraftState.isOpen, flyoutDraftState.editingProductId, flyoutDraftImageFetchKey]);

  // Committed selection enrichment: triggered after Done when selections may be
  // missing imageUrl (e.g., pre-selected options whose flyout enrichment hadn't completed)
  // or the cover's after-promotion price.
  //
  // fetchAndPatchImagesRef (defined above) keeps a stable ref so the effect body uses the
  // latest selections without itself being a dependency. We re-run on a signature of the
  // committed selection's product IDs (cover + options) so it fires for BOTH:
  //  - Complex (mode 'set'/'customize' — productId changes per set), AND
  //  - Simple inline (KINDW-1119/1121 — `commitInlineSelections` keeps `mode: null`, so the
  //    old `!committedSelection.mode` guard skipped enrichment entirely). On Simple PDPs the
  //    cover is built from `c_allVariantsData` (sale-book `salePrice`, no promotions), so
  //    without this the selected variant's price never upgrades to the after-promotion price
  //    even though options (built via buildOptionSlotSelectionFromProduct) already do.
  //    See docs/product/pricing-architecture.md §3b.
  const committedSelectionsRef = useRef(committedSelection.selections);

  committedSelectionsRef.current = committedSelection.selections;

  const committedSelectionSignature = useMemo(() => {
    const sel = committedSelection.selections;

    if (!sel || typeof sel !== 'object') return '';

    const parts = [];

    if (sel.cover?.productId) parts.push(`cover:${sel.cover.productId}`);

    Object.entries(sel).forEach(([stepId, arr]) => {
      if (stepId === 'cover' || !Array.isArray(arr)) return;

      arr.forEach((s) => {
        if (s?.productId) parts.push(`${stepId}:${s.productId}`);
      });
    });

    return parts.sort().join('|');
  }, [committedSelection.selections]);

  useEffect(() => {
    if (flyoutDraftState.isOpen) return;

    const selections = committedSelectionsRef.current;

    if (!selections || Object.keys(selections).length === 0) return;

    // Switch-back restore: when the shopper re-selects a previously-enriched cover,
    // `handleFreeformCoverSelect` rebuilds the cover from `c_allVariantsData` (sale-book
    // `salePrice`, no promotion) AND `fetchAndPatchImages` skips the network re-fetch
    // because the id is already in `coverEnrichmentRef` — so `fetchedMap[coverId]` is
    // undefined and the fetch-based promo patch below never runs, leaving the original
    // variant stuck on its pre-promotion price. Mirror the predefined-set restore
    // (`selectSet`) by re-applying the cached after-promotion price synchronously, so it
    // works even when nothing needs fetching (the callback may not fire at all).
    // See docs/product/pricing-architecture.md §3c.
    const coverId = selections.cover?.productId ?? null;
    const cachedCover = coverId ? coverEnrichmentRef.current[coverId] : null;

    if (cachedCover) {
      setCommittedSelection((prev) => {
        const cover = prev.selections.cover;

        if (!cover?.productId) return prev;

        const cached = coverEnrichmentRef.current[cover.productId];

        if (!cached) return prev;

        // Skip when the live cover already matches the cached enriched values — avoids an
        // update loop (the fetch-path cache write at the bottom uses the same numbers).
        if (
          cover.price === cached.price &&
          cover.salePrice === cached.salePrice &&
          (cached.imageUrl == null || cover.imageUrl === cached.imageUrl)
        ) {
          return prev;
        }

        return {
          ...prev,
          selections: {
            ...prev.selections,
            cover: {
              ...cover,
              price: cached.price,
              salePrice: cached.salePrice,
              ...(cached.imageUrl ? {imageUrl: cached.imageUrl} : {}),
            },
          },
        };
      });
    }

    fetchAndPatchImagesRef.current(selections, (fetchedMap) => {
      setCommittedSelection((prev) => {
        const updated = {...prev.selections};
        let changed = false;

        // Enrich cover: list price from c_priceProcessed, sale price from promotions
        if (updated.cover?.productId) {
          const fetchedCover = fetchedMap[updated.cover.productId];

          if (fetchedCover) {
            const coverPatch = {};

            if (updated.cover.price == null) {
              const coverListPrice = getProductListPrice(fetchedCover);

              if (coverListPrice != null) coverPatch.price = coverListPrice;
            }

            const coverPromoPrice = getProductPromotionalPrice(fetchedCover);
            const currentSale = updated.cover.salePrice ?? updated.cover.price;

            if (coverPromoPrice != null && (currentSale == null || coverPromoPrice < currentSale)) {
              coverPatch.salePrice = coverPromoPrice;
            }

            // Override the cover swatch with the variant's c_pimlyConfigurationImage[0] now that the
            // full cover product is available (same pimlyConfigurationImage-first rule as set/options).
            const coverImg = resolveSwatchThumbnailUrl(fetchedCover);

            if (coverImg && coverImg !== updated.cover.imageUrl) coverPatch.imageUrl = coverImg;

            if (Object.keys(coverPatch).length > 0) {
              updated.cover = {...updated.cover, ...coverPatch};
              changed = true;
            }

            coverEnrichmentRef.current[updated.cover.productId] = {
              price: updated.cover.price,
              salePrice: updated.cover.salePrice,
              imageUrl: updated.cover.imageUrl,
            };
          }
        }

        Object.entries(updated).forEach(([stepId, slotArr]) => {
          if (stepId === 'cover' || !Array.isArray(slotArr)) return;

          updated[stepId] = slotArr.map((slot) => {
            if (!slot?.productId) return slot;

            const fetched = fetchedMap[slot.productId];

            if (!fetched) return slot;

            const patch = patchSlotData(slot, fetched);

            if (!patch) return slot;

            changed = true;

            return {...slot, ...patch};
          });
        });

        if (!changed) return prev;

        return {...prev, selections: updated};
      });
    });
  }, [committedSelection.mode, committedSelection.productId, committedSelectionSignature, flyoutDraftState.isOpen]);

  const value = useMemo(
    () => ({
      committedSelection,
      flyoutDraftState,
      inlineSelectionsDraft,
      setInlineSelectionsDraft,
      setCards: enrichedSetCards,
      setCardTotals,
      setCardListTotals,
      setProductsMap,
      setOptionPriceMap,
      setOptionListPriceMap,
      setOptionImageMap,
      isLoadingSetCards,
      isFetchingSetOptionPrices,
      selectSet,
      editSet,
      switchToCustomizeInFlyout,
      openFlyout,
      updateDraftSelection,
      markFlyoutUserInteracted,
      commitFlyout,
      cancelFlyout,
      commitInlineSelections,
      resetJourney,
      updateGalleryPreview,
      // Safe mode change methods with confirmation check
      shouldConfirmModeChange,
      selectSetSafe,
      openFlyoutSafe,
      editSetSafe,
    }),
    [
      committedSelection,
      flyoutDraftState,
      inlineSelectionsDraft,
      enrichedSetCards,
      setCardTotals,
      setCardListTotals,
      setProductsMap,
      setOptionPriceMap,
      setOptionListPriceMap,
      setOptionImageMap,
      isLoadingSetCards,
      isFetchingSetOptionPrices,
      selectSet,
      editSet,
      switchToCustomizeInFlyout,
      openFlyout,
      updateDraftSelection,
      markFlyoutUserInteracted,
      commitFlyout,
      cancelFlyout,
      commitInlineSelections,
      resetJourney,
      updateGalleryPreview,
      shouldConfirmModeChange,
      selectSetSafe,
      openFlyoutSafe,
      editSetSafe
    ]
  );

  return (
    <CustomizeJourneyContext.Provider value={value}>
      {children}
    </CustomizeJourneyContext.Provider>
  );
}

CustomizeJourneyProvider.displayName = 'CustomizeJourneyProvider';

CustomizeJourneyProvider.propTypes = {
  product: PropTypes.object.isRequired,
  siteId: PropTypes.string,
  children: PropTypes.node.isRequired,
};
